import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { ScrollArea } from "./ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Separator } from "./ui/separator";
import { Progress } from "./ui/progress";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { toast } from "sonner";
import axios from "axios";
import PDFReader from "./PDFReader";
import {
  BookOpen,
  FolderOpen,
  ClipboardCheck,
  FileText,
  ExternalLink,
  File,
  Image,
  Video,
  Link as LinkIcon,
  CheckCircle,
  XCircle,
  MinusCircle,
  Loader2,
  Calendar,
  ChevronRight,
  Info,
  HandHeart,
  Send,
  MessageCircle,
  Heart,
  User,
  Eye,
  CalendarDays,
  MapPin,
  Clock
} from "lucide-react";
import { APPT_STATUS, fmtDateBR } from "./ProjectAgendaTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StudentAreaModal({ 
  open, 
  onOpenChange, 
  projectId,
  projectTitle,
  projectType  // "cursos", "oracao", "terapias", etc.
}) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState("materiais");
  const [expandedSection, setExpandedSection] = useState(null);
  
  // Estado para PDF Reader
  const [pdfReaderOpen, setPdfReaderOpen] = useState(false);
  const [selectedPdf, setSelectedPdf] = useState(null);
  
  // Estado para novo pedido de oração
  const [novoPedido, setNovoPedido] = useState("");
  const [enviandoPedido, setEnviandoPedido] = useState(false);

  useEffect(() => {
    if (open && projectId) {
      loadStudentArea();
    }
  }, [open, projectId]);

  const loadStudentArea = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/projects/${projectId}/student-area`);
      setData(response.data);
      
      // Determine tipo do projeto
      const tipo = response.data.projeto?.tipo || projectType;
      
      // Set default tab based on project type and enabled modules
      if (tipo === "oracao") {
        setActiveTab("oracao");
      } else if (tipo === "terapias") {
        setActiveTab("agenda");
      } else if (response.data.modules?.materiais) {
        setActiveTab("materiais");
      } else if (response.data.modules?.presenca) {
        setActiveTab("presenca");
      } else if (response.data.modules?.formulario) {
        setActiveTab("formulario");
      } else {
        setActiveTab("info");
      }
    } catch (error) {
      console.error("Error loading student area:", error);
      toast.error("Erro ao carregar área do membro");
    } finally {
      setLoading(false);
    }
  };

  const getMaterialIcon = (tipo) => {
    switch (tipo) {
      case "pdf": return <File className="h-5 w-5 text-red-500" />;
      case "doc": return <FileText className="h-5 w-5 text-blue-500" />;
      case "image": return <Image className="h-5 w-5 text-green-500" />;
      case "video": return <Video className="h-5 w-5 text-purple-500" />;
      default: return <LinkIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getAttendanceIcon = (presente) => {
    if (presente === true) return <CheckCircle className="h-5 w-5 text-green-500" />;
    if (presente === false) return <XCircle className="h-5 w-5 text-red-500" />;
    return <MinusCircle className="h-5 w-5 text-gray-300" />;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr + "T12:00:00");
      return date.toLocaleDateString("pt-BR", { 
        weekday: "short", 
        day: "2-digit", 
        month: "short" 
      });
    } catch (e) {
      return dateStr;
    }
  };

  const isToday = (dateStr) => {
    const today = new Date().toISOString().split('T')[0];
    return dateStr === today;
  };

  const isPast = (dateStr) => {
    const today = new Date().toISOString().split('T')[0];
    return dateStr < today;
  };

  // Determina se é projeto de oração
  const isOracao = data?.projeto?.tipo === "oracao" || projectType === "oracao";
  const isTerapia = data?.projeto?.tipo === "terapias" || projectType === "terapias";
  
  // Função para enviar novo pedido de oração
  const enviarPedidoOracao = async () => {
    if (!novoPedido.trim()) {
      toast.error("Digite seu pedido de oração");
      return;
    }
    
    setEnviandoPedido(true);
    try {
      await axios.post(`${API}/projects/${projectId}/prayer-request`, {
        descricao: novoPedido
      });
      toast.success("Pedido de oração enviado!");
      setNovoPedido("");
      loadStudentArea(); // Recarrega os dados
    } catch (error) {
      toast.error("Erro ao enviar pedido");
    } finally {
      setEnviandoPedido(false);
    }
  };
  
  // Título dinâmico baseado no tipo
  const getModalTitle = () => {
    if (isOracao) return "Meus Pedidos";
    if (isTerapia) return "Minha Agenda";
    return "Área do Membro";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg h-[85vh] flex flex-col p-0">
        <DialogHeader className="p-4 pb-2 border-b shrink-0">
          <div className="flex items-center gap-2">
            {isOracao ? (
              <HandHeart className="h-5 w-5 text-primary" />
            ) : (
              <BookOpen className="h-5 w-5 text-primary" />
            )}
            <DialogTitle className="text-lg">{getModalTitle()}</DialogTitle>
          </div>
          <p className="text-sm text-muted-foreground">{projectTitle || data?.projeto?.titulo}</p>
        </DialogHeader>

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : !data ? (
          <div className="flex-1 flex items-center justify-center p-4">
            <p className="text-muted-foreground">Não foi possível carregar os dados</p>
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
            <TabsList className="mx-4 mt-2 shrink-0 flex gap-1 p-1 bg-muted/50 rounded-full overflow-x-auto w-fit max-w-[calc(100%-2rem)]">
              {/* Tab de Oração - apenas para projetos de oração */}
              {isOracao && (
                <TabsTrigger value="oracao" className="rounded-full px-3 py-1.5 text-xs data-[state=active]:bg-primary data-[state=active]:text-primary-foreground whitespace-nowrap">
                  <HandHeart className="h-3.5 w-3.5 mr-1" />
                  <span className="hidden sm:inline">Pedidos</span>
                </TabsTrigger>
              )}
              {/* Tab Agenda - terapias */}
              {isTerapia && (
                <TabsTrigger value="agenda" className="rounded-full px-3 py-1.5 text-xs data-[state=active]:bg-primary data-[state=active]:text-primary-foreground whitespace-nowrap" data-testid="student-tab-agenda">
                  <CalendarDays className="h-3.5 w-3.5 mr-1" />
                  <span className="hidden sm:inline">Agenda</span>
                </TabsTrigger>
              )}
              {/* Tab de Materiais - não mostrar para oração */}
              {!isOracao && data.modules?.materiais && (
                <TabsTrigger value="materiais" className="rounded-full px-3 py-1.5 text-xs data-[state=active]:bg-primary data-[state=active]:text-primary-foreground whitespace-nowrap">
                  <FolderOpen className="h-3.5 w-3.5 mr-1" />
                  <span className="hidden sm:inline">Materiais</span>
                </TabsTrigger>
              )}
              {/* Tab de Presença/Sessões - não mostrar para oração */}
              {!isOracao && data.modules?.presenca && (
                <TabsTrigger value="presenca" className="rounded-full px-3 py-1.5 text-xs data-[state=active]:bg-primary data-[state=active]:text-primary-foreground whitespace-nowrap">
                  <ClipboardCheck className="h-3.5 w-3.5 mr-1" />
                  <span className="hidden sm:inline">{isTerapia ? "Sessões" : "Presença"}</span>
                </TabsTrigger>
              )}
              {/* Tab de Formulário */}
              {data.modules?.formulario && (
                <TabsTrigger value="formulario" className="rounded-full px-3 py-1.5 text-xs data-[state=active]:bg-primary data-[state=active]:text-primary-foreground whitespace-nowrap">
                  <FileText className="h-3.5 w-3.5 mr-1" />
                  <span className="hidden sm:inline">Formulário</span>
                </TabsTrigger>
              )}
              <TabsTrigger value="info" className="rounded-full px-3 py-1.5 text-xs data-[state=active]:bg-primary data-[state=active]:text-primary-foreground whitespace-nowrap">
                <Info className="h-3.5 w-3.5 mr-1" />
                <span className="hidden sm:inline">Info</span>
              </TabsTrigger>
            </TabsList>

            <ScrollArea className="flex-1">
              {/* TAB ORAÇÃO - apenas para projetos de oração */}
              {isOracao && (
                <TabsContent value="oracao" className="p-4 m-0 space-y-4">
                  {/* Novo pedido de oração */}
                  <div className="space-y-2 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-100">
                    <Label className="text-sm font-medium text-indigo-800 flex items-center gap-2">
                      <Heart className="h-4 w-4" />
                      Novo Pedido de Oração
                    </Label>
                    <Textarea
                      value={novoPedido}
                      onChange={(e) => setNovoPedido(e.target.value)}
                      placeholder="Descreva seu pedido de oração..."
                      rows={3}
                      className="resize-none"
                    />
                    <Button 
                      onClick={enviarPedidoOracao} 
                      disabled={enviandoPedido || !novoPedido.trim()}
                      className="w-full"
                    >
                      {enviandoPedido ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Send className="h-4 w-4 mr-2" />
                      )}
                      Enviar Pedido
                    </Button>
                  </div>
                  
                  <Separator />
                  
                  {/* Histórico de pedidos */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm text-muted-foreground flex items-center gap-2">
                      <MessageCircle className="h-4 w-4" />
                      Meus Pedidos Anteriores
                    </h4>
                    {data.pedidos_oracao?.length > 0 ? (
                      data.pedidos_oracao.map((pedido, idx) => (
                        <div 
                          key={pedido.id || idx}
                          className="p-4 rounded-xl border bg-card space-y-2"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-sm">{pedido.descricao}</p>
                            <Badge variant="outline" className="text-xs shrink-0">
                              {formatDate(pedido.data)}
                            </Badge>
                          </div>
                          {pedido.resposta && (
                            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                              <p className="text-xs text-green-800 font-medium mb-1">Resposta do líder:</p>
                              <p className="text-sm text-green-700">{pedido.resposta}</p>
                            </div>
                          )}
                          {pedido.status && (
                            <Badge 
                              variant={pedido.status === "atendido" ? "default" : "secondary"}
                              className={pedido.status === "atendido" ? "bg-green-500" : ""}
                            >
                              {pedido.status === "atendido" ? "Orado" : pedido.status}
                            </Badge>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8">
                        <HandHeart className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
                        <p className="text-muted-foreground">Nenhum pedido de oração ainda</p>
                        <p className="text-sm text-muted-foreground">Envie seu primeiro pedido acima</p>
                      </div>
                    )}
                  </div>
                </TabsContent>
              )}
              
              {/* TAB AGENDA - terapias */}
              {isTerapia && (
                <TabsContent value="agenda" className="p-4 m-0 space-y-3" data-testid="student-agenda-content">
                  {(() => {
                    const today = new Date().toISOString().split("T")[0];
                    const agenda = data.agenda || [];
                    const proximas = agenda.filter(a => a.data >= today && a.status === "agendado");
                    const passadas = agenda.filter(a => !(a.data >= today && a.status === "agendado")).reverse();
                    const Item = ({ a }) => {
                      const st = APPT_STATUS.find(x => x.value === a.status) || APPT_STATUS[0];
                      return (
                        <div className="flex items-center gap-3 p-3 rounded-xl border bg-card" data-testid={`student-appointment-${a.id}`}>
                          <div className="text-center w-14 shrink-0">
                            <p className="text-xs text-muted-foreground">{fmtDateBR(a.data).slice(0, 5)}</p>
                            <p className="text-lg font-bold leading-none">{a.hora}</p>
                          </div>
                          <div className="flex-1 min-w-0 text-xs text-muted-foreground space-y-0.5">
                            <p className="text-sm font-medium text-foreground">{fmtDateBR(a.data)} · {a.duracao_min} min</p>
                            {a.local && <p className="flex items-center gap-1"><MapPin className="h-3 w-3" />{a.local}</p>}
                            {a.observacoes && <p>{a.observacoes}</p>}
                          </div>
                          <Badge variant="outline" className={`text-[10px] shrink-0 ${st.cls}`}>{st.label}</Badge>
                        </div>
                      );
                    };
                    return (
                      <>
                        <p className="text-xs font-semibold text-primary flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Próximas sessões</p>
                        {proximas.length === 0 ? (
                          <div className="text-center py-6 text-muted-foreground">
                            <CalendarDays className="h-10 w-10 mx-auto mb-2 opacity-40" />
                            <p className="text-sm">Nenhuma sessão agendada</p>
                          </div>
                        ) : proximas.map(a => <Item key={a.id} a={a} />)}
                        {passadas.length > 0 && (
                          <>
                            <p className="text-xs font-semibold text-muted-foreground pt-2">Histórico</p>
                            {passadas.map(a => <Item key={a.id} a={a} />)}
                          </>
                        )}
                      </>
                    );
                  })()}
                </TabsContent>
              )}

              {/* TAB MATERIAIS */}
              <TabsContent value="materiais" className="p-4 m-0">
                <div className="space-y-3">
                  {data.materiais?.length > 0 ? (
                    data.materiais.map((material) => {
                      const isPdf = material.tipo === "pdf" || material.url?.toLowerCase().endsWith('.pdf');
                      const materialUrl = material.url?.startsWith('/') 
                        ? `${process.env.REACT_APP_BACKEND_URL}${material.url}` 
                        : material.url;
                      
                      return (
                        <div
                          key={material.id}
                          className="flex items-center gap-3 p-4 border rounded-xl hover:bg-muted/50 transition-colors group cursor-pointer"
                          onClick={() => {
                            if (isPdf) {
                              setSelectedPdf({ url: materialUrl, title: material.titulo });
                              setPdfReaderOpen(true);
                            } else {
                              window.open(materialUrl, "_blank");
                            }
                          }}
                        >
                          <div className="p-2 bg-muted rounded-lg">
                            {getMaterialIcon(material.tipo)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium group-hover:text-primary transition-colors">
                              {material.titulo}
                            </p>
                            {material.descricao && (
                              <p className="text-sm text-muted-foreground truncate">
                                {material.descricao}
                              </p>
                            )}
                          </div>
                          {isPdf ? (
                            <Eye className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
                          ) : (
                            <ExternalLink className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-12">
                      <FolderOpen className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
                      <p className="text-muted-foreground">Nenhum material disponível ainda</p>
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* TAB PRESENÇA/SESSÕES */}
              <TabsContent value="presenca" className="p-4 m-0 space-y-4">
                {data.presenca?.summary && (
                  <div className="p-4 bg-gradient-to-r from-primary/10 to-primary/5 rounded-xl space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">{isTerapia ? "Sessões" : "Frequência"}</span>
                      <span className="text-2xl font-bold text-primary">
                        {data.presenca.summary.percentual}%
                      </span>
                    </div>
                    <Progress value={data.presenca.summary.percentual} className="h-2" />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>{data.presenca.summary.presencas} {isTerapia ? "sessões realizadas" : "presenças"}</span>
                      <span>{data.presenca.summary.faltas} {isTerapia ? "ausências" : "faltas"}</span>
                      <span>{data.presenca.summary.total_aulas} {isTerapia ? "sessões totais" : "aulas"}</span>
                    </div>
                  </div>
                )}

                <Separator />

                <div className="space-y-2">
                  <h4 className="font-semibold text-sm text-muted-foreground">Histórico</h4>
                  {data.presenca?.history?.length > 0 ? (
                    <div className="space-y-2">
                      {data.presenca.history.map((item, index) => (
                        <div
                          key={index}
                          className={`flex items-center gap-3 p-3 rounded-lg border ${
                            isToday(item.data) 
                              ? "bg-primary/10 border-primary/30" 
                              : isPast(item.data) 
                                ? "bg-muted/30" 
                                : ""
                          }`}
                        >
                          {getAttendanceIcon(item.presente)}
                          <div className="flex-1">
                            <p className={`font-medium ${isToday(item.data) ? "text-primary" : ""}`}>
                              {formatDate(item.data)}
                              {isToday(item.data) && (
                                <Badge className="ml-2 text-xs">Hoje</Badge>
                              )}
                            </p>
                            {item.observacao && (
                              <p className="text-xs text-muted-foreground">{item.observacao}</p>
                            )}
                          </div>
                          <span className={`text-xs font-medium ${
                            item.presente === true ? "text-green-600" :
                            item.presente === false ? "text-red-600" :
                            "text-muted-foreground"
                          }`}>
                            {item.presente === true ? "Presente" :
                             item.presente === false ? "Ausente" :
                             "Pendente"}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <ClipboardCheck className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
                      <p className="text-muted-foreground">Nenhum registro de presença</p>
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* TAB FORMULÁRIO */}
              <TabsContent value="formulario" className="p-4 m-0 space-y-4">
                <h4 className="font-semibold text-sm text-muted-foreground">
                  Suas Respostas do Formulário
                </h4>
                
                {data.formulario?.perguntas?.length > 0 ? (
                  <div className="space-y-4">
                    {data.formulario.perguntas.map((pergunta, index) => {
                      const resposta = data.formulario.respostas?.[pergunta.id];
                      return (
                        <div key={pergunta.id} className="border rounded-lg p-4">
                          <p className="font-medium text-sm">
                            {index + 1}. {pergunta.titulo}
                            {pergunta.obrigatoria && (
                              <span className="text-red-500 ml-1">*</span>
                            )}
                          </p>
                          {pergunta.descricao && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {pergunta.descricao}
                            </p>
                          )}
                          <div className="mt-2 p-3 bg-muted/50 rounded-md">
                            {resposta ? (
                              Array.isArray(resposta) ? (
                                <ul className="list-disc list-inside text-sm">
                                  {resposta.map((r, i) => <li key={i}>{r}</li>)}
                                </ul>
                              ) : (
                                <p className="text-sm">{resposta}</p>
                              )
                            ) : (
                              <p className="text-sm text-muted-foreground italic">
                                Não respondido
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <FileText className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
                    <p className="text-muted-foreground">Nenhum formulário preenchido</p>
                  </div>
                )}
              </TabsContent>

              {/* TAB INFO */}
              {/* TAB INFO */}
              <TabsContent value="info" className="p-4 m-0 space-y-4">
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold mb-2">{data.projeto?.titulo}</h4>
                    {data.projeto?.descricao && (
                      <p className="text-sm text-muted-foreground">
                        {data.projeto.descricao}
                      </p>
                    )}
                  </div>

                  {data.projeto?.observacoes_aluno && (
                    <>
                      <Separator />
                      <div className="p-4 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                        <h5 className="font-medium text-amber-800 dark:text-amber-200 mb-2 flex items-center gap-2">
                          <Info className="h-4 w-4" />
                          Observações do Líder
                        </h5>
                        <p className="text-sm text-amber-700 dark:text-amber-300 whitespace-pre-wrap">
                          {data.projeto.observacoes_aluno}
                        </p>
                      </div>
                    </>
                  )}

                  {data.is_leader && (
                    <div className="p-3 bg-primary/10 rounded-lg">
                      <Badge>Você é líder deste projeto</Badge>
                    </div>
                  )}
                </div>
              </TabsContent>
            </ScrollArea>
          </Tabs>
        )}
      </DialogContent>
      
      {/* PDF Reader Modal */}
      <PDFReader
        open={pdfReaderOpen}
        onOpenChange={setPdfReaderOpen}
        pdfUrl={selectedPdf?.url}
        title={selectedPdf?.title}
      />
    </Dialog>
  );
}
